import React, { useState, useEffect, useRef } from 'react';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import { generateDetailedReportPDF } from '../../utils/pdfGenerator';
import FilterDropdown from './FilterDropdown';

const ExportReportModal = ({ studentId, isOpen, onClose }) => {
    const { get } = useApi();
    const { showToast } = useAppState();

    const [terms, setTerms] = useState([]);
    const [academicYears, setAcademicYears] = useState([]);
    const [exportTermId, setExportTermId] = useState('');
    const [exportReportFormat, setExportReportFormat] = useState('');
    const [reportExporting, setReportExporting] = useState(false);

    // Drag to close logic for mobile
    const [dragY, setDragY] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const startY = useRef(0);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            // Reset state when modal is closed
            setExportTermId('');
            setExportReportFormat('');
            setDragY(0);
            return;
        }

        const fetchTermsAndYears = async () => {
            try {
                const [termsData, yearsData] = await Promise.all([
                    get('/api/academic-terms/'),
                    get('/api/academic-years/')
                ]);
                setTerms(termsData || []);
                setAcademicYears(yearsData || []);
                
                if (termsData && termsData.length > 0) {
                    const currentTerm = termsData.find(t => t.is_current) || termsData[0];
                    setExportTermId(currentTerm.id.toString());
                }
            } catch (e) {
                console.error("Failed to fetch academic metadata", e);
            }
        };

        fetchTermsAndYears();
    }, [isOpen]); // eslint-disable-line

    const onTouchStart = (e) => {
        startY.current = e.touches[0].clientY;
        setIsDragging(true);
    };

    const onTouchMove = (e) => {
        if (!isDragging) return;
        const currentY = e.touches[0].clientY;
        const diff = currentY - startY.current;
        if (diff > 0) {
            setDragY(diff);
        }
    };

    const onTouchEndHandler = () => {
        setIsDragging(false);
        if (dragY > 100) {
            onClose();
        }
        setDragY(0);
    };

    const handleExportReport = async () => {
        if (!exportTermId) { showToast('Please select a term first', 'error'); return; }
        if (!studentId) { showToast('Student ID is missing', 'error'); return; }
        if (!exportReportFormat) { showToast('Please select a report format', 'error'); return; }
        
        try {
            setReportExporting(true);
            let exportReportType = 'all';
            let exportReportPeriod = 'full_term';
            
            if (exportReportFormat === 'combined') {
                exportReportType = 'all';
                exportReportPeriod = 'full_term';
            } else if (exportReportFormat === 'end_term') {
                exportReportType = 'summative';
                exportReportPeriod = 'end_term';
            } else if (exportReportFormat === 'mid_term') {
                exportReportType = 'summative';
                exportReportPeriod = 'mid_term';
            } else if (exportReportFormat === 'formative') {
                exportReportType = 'formative';
                exportReportPeriod = '';
            }

            let url = `/api/cbc/reports/student/${studentId}/?term=${exportTermId}&report_type=${exportReportType}`;
            if (exportReportPeriod) {
                url += `&report_period=${exportReportPeriod}`;
            }
            const reportData = await get(url);
            const selectedTermObj = terms.find(t => t.id.toString() === exportTermId.toString());
            
            const selectedYearId = selectedTermObj?.year;
            const selectedYearObj = academicYears.find(y => y.id.toString() === selectedYearId?.toString());
            
            await generateDetailedReportPDF(reportData, {
                schoolName: 'Kianda School',
                termLabel: selectedTermObj ? selectedTermObj.name : 'Term',
                yearLabel: selectedYearObj ? selectedYearObj.name : new Date().getFullYear().toString(),
                reportPeriod: exportReportPeriod,
                reportType: exportReportType,
            });
            onClose();
        } catch (e) {
            console.error(e);
            showToast('Failed to generate report PDF', 'error');
        } finally {
            setReportExporting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            ></div>

            {/* Modal Content / Bottom Sheet */}
            <div 
                className={`relative bg-white rounded-t-[2.5rem] sm:rounded-[2rem] w-full max-w-[500px] shadow-2xl overflow-hidden sm:scale-100 max-h-[90vh] overflow-y-auto custom-scrollbar ${dragY === 0 && !isDragging ? 'animate-in slide-in-from-bottom-full sm:slide-in-from-bottom-4 duration-300' : ''}`}
                style={{ 
                    transform: `translateY(${dragY}px)`, 
                    transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)' 
                }}
            >
                {/* Mobile drag handle */}
                <div 
                    className="w-full flex justify-center pt-4 pb-2 sm:hidden absolute top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md cursor-pointer touch-none"
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEndHandler}
                >
                    <div className="w-12 h-1.5 bg-slate-200 rounded-full"></div>
                </div>

                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50/50 rounded-bl-full -mr-16 -mt-16 pointer-events-none"></div>
                
                <div className="px-8 pt-10 sm:pt-8 pb-4 flex items-center justify-between relative z-10 sticky top-0 sm:top-auto bg-white/80 backdrop-blur-md sm:bg-transparent">
                    <div>
                        <h3 className="text-xl font-black text-[#18216D] uppercase tracking-tight mt-1">
                            Report Card PDF
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="px-8 pb-8 space-y-5 relative z-10">
                    <div className="space-y-4 bg-slate-50 p-5 rounded-2xl">
                        <div>
                            <label className="text-[9px] font-black text-[#18216D] uppercase tracking-[0.2em] block mb-1.5">Academic Term</label>
                            <FilterDropdown
                                value={exportTermId}
                                onChange={(val) => setExportTermId(val)}
                                options={Array.isArray(terms) ? terms.map(t => ({ 
                                    value: t.id?.toString(), 
                                    name: `${t.name} ${t.year_name ? `— ${t.year_name}` : ''}` 
                                })) : []}
                                placeholder="Select Term"
                                className="w-full"
                            />
                        </div>
                        <div>
                            <label className="text-[9px] font-black text-[#18216D] uppercase tracking-[0.2em] block mb-1.5 mt-4">Report Format</label>
                            <FilterDropdown
                                value={exportReportFormat}
                                onChange={(val) => setExportReportFormat(val)}
                                options={[
                                    { value: 'combined', name: 'Combined Average' },
                                    { value: 'end_term', name: 'End Term Exam' },
                                    { value: 'mid_term', name: 'Mid Term Exam' },
                                    { value: 'formative', name: 'Formative Assessments' }
                                ]}
                                placeholder="Select Format"
                                className="w-full"
                            />
                        </div>
                    </div>

                    <button
                        onClick={handleExportReport}
                        disabled={reportExporting || !exportReportFormat}
                        className="w-full py-4 bg-[#18216D] text-white rounded-2xl font-black text-[11px] uppercase tracking-[0.2em] shadow-lg shadow-indigo-900/20 hover:bg-indigo-700 transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {reportExporting ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                                Generating...
                            </>
                        ) : (
                            <>
                                <DocumentArrowDownIcon className="w-5 h-5" />
                                Generate PDF
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExportReportModal;
